import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'
import { Code as CodeIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { v4 as uuidv4 } from 'uuid'

import { MobileAction, MOBILE_ACTION_CODE, MobileScreen, MOBILE_EVENT_CODE, MobileEventFlow, GETCommand } from '@mobileTypes'
import { useMobileScreens, useMobileForms } from '@mobileHooks'
import {
  MOBILE_EVENT_CONTEXTS,
  EventContextParam,
  COMMON_SCRIPT_VARIABLES,
  PREVIOUS_RESULT_OBJECT_READ_ALL_SCRIPT_VARIABLE,
  OBJECT_READ_ALL_COMMAND_NAME,
  MOBILE_ACTION_RESULT_TYPES,
} from '@mobileConstants'
import { AddCommandsDialog } from '@library'
import {
  ModalRefPathPicker,
  type RefPathValue,
} from '@libraryComponents/ModalRefPathPicker/ModalRefPathPicker'
import { IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { ScriptValueDialog, Variable, FieldInfo, variablesToFieldsInfo } from '@microservices/wiskey-react-components'
import { useInferredType } from '@mobileHooks'
import { Alert } from '@mui/material'
import { inferTypeFromCode } from '@mobileHelpers'

interface ActionConfigDialogProps {
  open: boolean;
  action: MobileAction | null;
  existingActions: MobileAction[];
  eventCode?: MOBILE_EVENT_CODE;
  formElements?: any[]; // Form elements for button/swipe selection
  variables?: Array<{ id?: string; internalId: string; objectCode?: string }>;
  globalVariables?: Array<{ id?: string; internalId: string; objectCode?: string }>;
  backgroundVariables?: Array<{ id?: string; internalId: string; objectCode?: string }>;
  sheetFormVariables?: Array<{ id?: string; internalId: string; objectCode?: string }>;
  eventFlows?: MobileEventFlow[];
  onClose: () => void;
  onSave: (action: MobileAction) => void;
  onCreateVariable?: (name: string, isGlobal: boolean, valueType?: string) => void;
}

interface ActionFormData {
  id: string;
  actionCode: MOBILE_ACTION_CODE | '';
  dependsOn: string[];
  screenId?: string;
  screenName?: string;
  navigationParams?: string;
  command?: GETCommand;
  variableName?: string;
  variableValue?: string;
  /** Scope for CHANGE_VARIABLE_VALUE: 'local' | 'global' | 'background' | 'sheetForm' (SPLIT only) */
  variableScope?: 'local' | 'global' | 'background' | 'sheetForm';
  jsCode?: string;
  // Interval fields
  intervalId?: string;
  intervalMs?: number;
  immediate?: boolean;
  noOverlap?: boolean;
  maxRuns?: number;
  // Modal form fields
  isModal?: boolean;
  formConfigId?: string;
  modalTitle?: string;
  onSubmitVariable?: string;
  // OPEN_SHEET action
  snapIndex?: number;
  // SHOW_TOAST action
  toastType?: 'success' | 'error' | 'info';
  toastText1?: string;
  toastText2?: string;
  // CONDITION action
  conditionType?: 'js' | 'variable';
  conditionJsCode?: string;
  conditionVariableName?: string;
  conditionVariableScope?: 'local' | 'global' | 'background' | 'sheetForm';
  conditionVariableFieldPath?: string;
  conditionVariablePathArray?: any[];
  conditionBranches?: Array<{ value: string; conditionBranchId: string }>;
  conditionDefaultBranchId?: string;
}

const ACTION_CODE_OPTIONS = Object.entries(MOBILE_ACTION_CODE)
  .map(([key, value]) => ({
    label: key.replace(/_/g, ' '),
    value,
  }))

export const ActionConfigDialog = ({
                                     open,
                                     action,
                                     existingActions,
                                     eventCode,
                                     formElements = [],
                                     variables = [],
                                     globalVariables = [],
                                     backgroundVariables,
                                     sheetFormVariables,
                                     eventFlows: eventFlowsProp = [],
                                     onClose,
                                     onSave,
                                     onCreateVariable,
                                   }: ActionConfigDialogProps) => {
  const { screens } = useMobileScreens()
  const { forms } = useMobileForms()
  const [showCommandDialog, setShowCommandDialog] = useState(false)
  const [showVariableValueEditor, setShowVariableValueEditor] = useState(false)
  const [showJsCodeEditor, setShowJsCodeEditor] = useState(false)
  const [showConditionJsEditor, setShowConditionJsEditor] = useState(false)

  const onConditionFlows = useMemo(
    () => eventFlowsProp.filter(f => f.eventCode === MOBILE_EVENT_CODE.ON_CONDITION && f.conditionBranchId),
    [eventFlowsProp]
  )

  const allVariables = useMemo(() => {
    const varKey = (v: { internalId: string }) => v.internalId
    const local = variables.map(v => ({ ...v, isGlobal: false, variableScope: 'local' as const }))
    const global = globalVariables.map(v => ({ ...v, isGlobal: true, variableScope: 'global' as const }))
    const background =
      backgroundVariables?.map(v => ({
        ...v,
        id: `bg-${v.id ?? v.internalId}`,
        internalId: varKey(v),
        isGlobal: false,
        variableScope: 'background' as const,
      })) ?? []
    const sheetForm =
      sheetFormVariables?.map(v => ({
        ...v,
        id: `sheet-${v.id ?? v.internalId}`,
        internalId: varKey(v),
        isGlobal: false,
        variableScope: 'sheetForm' as const,
      })) ?? []
    return [...local, ...global, ...background, ...sheetForm]
  }, [variables, globalVariables, backgroundVariables, sheetFormVariables])

  const availableContextParams: EventContextParam[] = useMemo(() => {
    if (!eventCode || !MOBILE_EVENT_CONTEXTS[eventCode]) {
      return []
    }
    return MOBILE_EVENT_CONTEXTS[eventCode].availableParams
  }, [eventCode])

  const { control, handleSubmit, reset, watch, setError, clearErrors, setValue } = useForm<ActionFormData>({
    defaultValues: {
      id: '',
      actionCode: '',
      dependsOn: [],
      screenId: '',
      screenName: '',
      navigationParams: '',
      command: undefined,
      variableName: '',
      variableValue: '',
      variableScope: 'local' as const,
      jsCode: '',
      intervalId: '',
      intervalMs: undefined,
      immediate: true,
      noOverlap: true,
      maxRuns: undefined,
      isModal: false,
      formConfigId: '',
      modalTitle: '',
      onSubmitVariable: '',
      snapIndex: 1,
      toastType: 'error' as const,
      toastText1: '',
      toastText2: '',
      conditionType: 'js' as const,
      conditionJsCode: '',
      conditionVariableName: '',
      conditionVariableScope: 'local' as const,
      conditionVariableFieldPath: '',
      conditionVariablePathArray: undefined,
      conditionBranches: [],
      conditionDefaultBranchId: '',
    },
  })

  const watchedDependsOn = watch('dependsOn')
  const watchedActionCode = watch('actionCode')

  const scriptVariablesForCommands: Variable[] = useMemo(() => {
    const eventDataChildren: Variable[] = []
    const varKey = (v: { internalId: string }) => v.internalId
    for (const param of availableContextParams) {
      if (param.name === 'globalVariables' && globalVariables?.length) continue
      if (param.name === 'backgroundVariables' && backgroundVariables?.length) continue
      if (param.name === 'sheetFormVariables' && sheetFormVariables?.length) continue
      eventDataChildren.push({
        name: param.name,
        type: param.type,
        description: param.description || '',
      })
    }
    if (variables?.length) {
      eventDataChildren.push({
        name: 'localVariables',
        type: variables.map(v => ({ name: varKey(v), type: 'any' })),
        description: 'Local variables',
      })
    }
    if (globalVariables?.length) {
      eventDataChildren.push({
        name: 'globalVariables',
        type: globalVariables.map(v => ({ name: varKey(v), type: 'any' })),
        description: 'Global variables',
      })
    }
    if (backgroundVariables?.length) {
      eventDataChildren.push({
        name: 'backgroundVariables',
        type: backgroundVariables.map(v => ({ name: varKey(v), type: 'any' })),
        description: 'Variables from background (list/form)',
      })
    }
    if (sheetFormVariables?.length) {
      eventDataChildren.push({
        name: 'sheetFormVariables',
        type: sheetFormVariables.map(v => ({ name: varKey(v), type: 'any' })),
        description: 'Variables from sheet form',
      })
    }
    // Add previousResult only when dependsOn points to EXECUTE_COMMAND with command object_read_all
    if (watchedDependsOn && watchedDependsOn.length === 1) {
      const dependentAction = existingActions.find(a => a.id === watchedDependsOn[0])
      const isObjectReadAll =
        dependentAction?.actionCode === MOBILE_ACTION_CODE.EXECUTE_COMMAND &&
        (dependentAction.meta?.command as { name?: string } | undefined)?.name === OBJECT_READ_ALL_COMMAND_NAME
      if (isObjectReadAll) {
        eventDataChildren.push(PREVIOUS_RESULT_OBJECT_READ_ALL_SCRIPT_VARIABLE)
      }
    }

    return [
      {
        name: 'eventData',
        type: eventDataChildren,
        description: 'Event context: params, localVariables, globalVariables, previousResult (when dependsOn is object_read_all)',
      },
      ...COMMON_SCRIPT_VARIABLES,
    ]
  }, [availableContextParams, variables, globalVariables, backgroundVariables, sheetFormVariables, watchedDependsOn, existingActions])

  const scriptFieldsInfoForCommands: FieldInfo[] = useMemo(
    () => variablesToFieldsInfo(scriptVariablesForCommands),
    [scriptVariablesForCommands]
  )

  useEffect(() => {
    if (open) {
      if (action) {
        // Migration: support old format where fields were at top level
        const screenId = action.meta?.screenId || (action as any).screenId || ''
        const screenName = action.meta?.screenName || (action as any).screenName || ''
        const navigationParams = action.meta?.navigationParams || (action as any).navigationParams || ''
        const command = action.meta?.command || (action as any).command || undefined
        const variableName = action.meta?.variableName || ''
        const variableValue = action.meta?.variableValue || ''
        const variableScope = action.meta?.variableScope || 'local'
        const jsCode = action.meta?.jsCode || ''

        // Interval fields
        const intervalId = action.meta?.intervalId || ''
        const intervalMs = action.meta?.intervalMs || undefined
        const immediate = action.meta?.immediate ?? true
        const noOverlap = action.meta?.noOverlap ?? true
        const maxRuns = action.meta?.maxRuns || undefined

        // Modal form fields
        const isModal = action.meta?.isModal || false
        const formConfigId = action.meta?.formConfigId || ''
        const modalTitle = action.meta?.modalTitle || ''
        const onSubmitVariable = action.meta?.onSubmitVariable || ''
        const snapIndex = action.meta?.snapIndex ?? 1

        // SHOW_TOAST fields
        const toastType = action.meta?.type || 'error'
        const toastText1 = action.meta?.text1 || ''
        const toastText2 = action.meta?.text2 || ''

        // CONDITION fields
        const conditionType = action.meta?.conditionType || 'js'
        const conditionJsCode = action.meta?.conditionJsCode || ''
        const conditionVariableName = action.meta?.conditionVariableName || ''
        const conditionVariableScope = action.meta?.conditionVariableScope || 'local'
        const conditionVariableFieldPath = action.meta?.conditionVariableFieldPath || ''
        const conditionVariablePathArray = action.meta?.conditionVariablePathArray || undefined
        const conditionBranches = action.meta?.conditionBranches || []
        const conditionDefaultBranchId = action.meta?.conditionDefaultBranchId || ''

        reset({
          id: action.id,
          actionCode: action.actionCode,
          dependsOn: action.dependsOn || [],
          screenId,
          screenName,
          navigationParams,
          command,
          variableName,
          variableValue,
          variableScope,
          jsCode,
          intervalId,
          intervalMs,
          immediate,
          noOverlap,
          maxRuns,
          isModal,
          formConfigId,
          modalTitle,
          onSubmitVariable,
          snapIndex,
          toastType,
          toastText1,
          toastText2,
          conditionType,
          conditionJsCode,
          conditionVariableName,
          conditionVariableScope,
          conditionVariableFieldPath,
          conditionVariablePathArray,
          conditionBranches,
          conditionDefaultBranchId,
        })
      } else {
        reset({
          id: uuidv4(),
          actionCode: '',
          dependsOn: [],
          screenId: '',
          screenName: '',
          navigationParams: '',
          command: undefined,
          variableName: '',
          variableValue: '',
          variableScope: 'local' as const,
          jsCode: '',
          intervalId: '',
          intervalMs: undefined,
          immediate: true,
          noOverlap: true,
          maxRuns: undefined,
          isModal: false,
          formConfigId: '',
          modalTitle: '',
          onSubmitVariable: '',
          snapIndex: 1,
          toastType: 'error' as const,
          toastText1: '',
          toastText2: '',
          conditionType: 'js' as const,
          conditionJsCode: '',
          conditionVariableName: '',
          conditionVariableScope: 'local' as const,
          conditionVariableFieldPath: '',
          conditionVariablePathArray: undefined,
          conditionBranches: [],
          conditionDefaultBranchId: '',
        })
      }
    }
  }, [open, action, reset])

  // Validate circular dependencies
  useEffect(() => {
    if (watchedDependsOn && watchedDependsOn.length > 0) {
      const hasCycle = checkCircularDependency(
        watch('id'),
        watchedDependsOn,
        existingActions.filter(a => a.id !== watch('id')),
      )

      if (hasCycle) {
        setError('dependsOn', {
          type: 'manual',
          message: 'Circular dependency detected',
        })
      } else {
        clearErrors('dependsOn')
      }
    }
  }, [watchedDependsOn, existingActions, watch, setError, clearErrors])

  const availableActionsForDependency = useMemo(() => {
    return existingActions.filter(a => a.id !== action?.id)
  }, [existingActions, action])

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    event.stopPropagation()

    handleSubmit((data: ActionFormData) => {
      const hasCycle = checkCircularDependency(
        data.id,
        data.dependsOn,
        existingActions.filter(a => a.id !== data.id),
      )

      if (hasCycle) {
        setError('dependsOn', {
          type: 'manual',
          message: 'Circular dependency detected',
        })

        return
      }

      if (!data.actionCode) {
        return
      }

      // Validate NAVIGATE action requires screenId
      if (data.actionCode === MOBILE_ACTION_CODE.NAVIGATE && !data.screenId) {
        setError('screenId', {
          type: 'manual',
          message: 'Screen is required for NAVIGATE action',
        })
        return
      }

      const actionData: MobileAction = {
        id: data.id,
        actionCode: data.actionCode as MOBILE_ACTION_CODE,
        dependsOn: data.dependsOn.length > 0 ? data.dependsOn : undefined,
        meta: {},
      }

      // Add screen info for NAVIGATE action to meta
      if (data.actionCode === MOBILE_ACTION_CODE.NAVIGATE) {
        actionData.meta = {
          ...actionData.meta,
        }

        if (data.screenId) {
          actionData.meta.screenId = data.screenId
          actionData.meta.screenName = data.screenName
        }

        if (data.navigationParams && data.navigationParams.trim()) {
          actionData.meta.navigationParams = data.navigationParams.trim()
        }

        // Modal form fields
        if (data.isModal) {
          actionData.meta.isModal = true
          if (data.formConfigId) {
            actionData.meta.formConfigId = data.formConfigId
          }
          if (data.modalTitle) {
            actionData.meta.modalTitle = data.modalTitle
          }
          if (data.onSubmitVariable) {
            actionData.meta.onSubmitVariable = data.onSubmitVariable
          }
        }
      }

      // Add command for EXECUTE_COMMAND action to meta
      if (data.actionCode === MOBILE_ACTION_CODE.EXECUTE_COMMAND) {
        if (!data.command) {
          setError('command' as any, {
            type: 'manual',
            message: 'Command is required for EXECUTE_COMMAND action',
          })
          return
        }
        actionData.meta = {
          ...actionData.meta,
          command: data.command,
        }
      }

      // Add variable info for CHANGE_VARIABLE_VALUE action to meta
      if (data.actionCode === MOBILE_ACTION_CODE.CHANGE_VARIABLE_VALUE) {
        if (!data.variableName) {
          setError('variableName' as any, {
            type: 'manual',
            message: 'Variable is required for CHANGE_VARIABLE_VALUE action',
          })
          return
        }
        actionData.meta = {
          ...actionData.meta,
          variableName: data.variableName,
          variableValue: data.variableValue || '',
          ...(data.variableScope && data.variableScope !== 'local' && data.variableScope !== 'global'
            ? { variableScope: data.variableScope }
            : {}),
        }
      }

      // Add JS code and inferred type for EXECUTE_JS action to meta
      if (data.actionCode === MOBILE_ACTION_CODE.EXECUTE_JS) {
        if (!data.jsCode || !data.jsCode.trim()) {
          setError('jsCode' as any, {
            type: 'manual',
            message: 'JavaScript code is required for EXECUTE_JS action',
          })
          return
        }

        // Build variable types map for type inference
        const variableTypesMap: Record<string, string> = {}
        availableContextParams.forEach(param => {
          variableTypesMap[param.name] = param.type
        })
        variables.forEach(v => {
          variableTypesMap[`localVariables.${v.internalId}`] = 'any'
        })
        globalVariables.forEach(v => {
          variableTypesMap[`globalVariables.${v.internalId}`] = 'any'
        })
        backgroundVariables?.forEach(v => {
          variableTypesMap[`backgroundVariables.${v.internalId}`] = 'any'
        })
        sheetFormVariables?.forEach(v => {
          variableTypesMap[`sheetFormVariables.${v.internalId}`] = 'any'
        })

        // TODO Расширить возможность зависимостей от нескольких экшенов
        // Add previousResult type when depends on one action (switch by actionCode)
        if (data.dependsOn && data.dependsOn.length === 1) {
          const dependentAction = existingActions.find(a => a.id === data.dependsOn[0])
          if (dependentAction) {
            switch (dependentAction.actionCode) {
              case MOBILE_ACTION_CODE.EXECUTE_JS: {
                const resultType = dependentAction.meta?.jsResultType || 'any'
                if (resultType !== 'void' && resultType !== 'unknown') {
                  variableTypesMap['previousResult'] = resultType
                }
                break
              }
              case MOBILE_ACTION_CODE.EXECUTE_COMMAND: {
                const commandName = (dependentAction.meta?.command as { name?: string } | undefined)?.name
                if (commandName === OBJECT_READ_ALL_COMMAND_NAME) {
                  variableTypesMap['previousResult'] = '{ data: any[]; totalPages: number; totalElements: number; currentPage: number; pageSize: number }'
                } else {
                  const resultTypeInfo = MOBILE_ACTION_RESULT_TYPES[dependentAction.actionCode]
                  if (resultTypeInfo && resultTypeInfo.type !== 'void') {
                    if (resultTypeInfo.fields) {
                      const fieldsStr = resultTypeInfo.fields
                        .map(f => `${f.name}: ${f.type}`)
                        .join('; ')
                      variableTypesMap['previousResult'] = `{ ${fieldsStr} }`
                    } else {
                      variableTypesMap['previousResult'] = resultTypeInfo.type
                    }
                  }
                }
                break
              }
              default: {
                const resultTypeInfo = MOBILE_ACTION_RESULT_TYPES[dependentAction.actionCode]
                if (resultTypeInfo && resultTypeInfo.type !== 'void') {
                  if (resultTypeInfo.fields) {
                    const fieldsStr = resultTypeInfo.fields
                      .map(f => `${f.name}: ${f.type}`)
                      .join('; ')
                    variableTypesMap['previousResult'] = `{ ${fieldsStr} }`
                  } else {
                    variableTypesMap['previousResult'] = resultTypeInfo.type
                  }
                }
                break
              }
            }
          }
        }

        // Infer type from code
        const inferred = inferTypeFromCode(data.jsCode, variableTypesMap)

        actionData.meta = {
          ...actionData.meta,
          jsCode: data.jsCode.trim(),
          jsResultType: inferred.type,
          jsResultFields: inferred.fields,
        }
      }

      // Add interval meta for START_INTERVAL action
      if (data.actionCode === MOBILE_ACTION_CODE.START_INTERVAL) {
        if (!data.intervalId || !data.intervalId.trim()) {
          setError('intervalId' as any, {
            type: 'manual',
            message: 'Interval ID is required for START_INTERVAL action',
          })
          return
        }

        if (!data.intervalMs || data.intervalMs <= 0) {
          setError('intervalMs' as any, {
            type: 'manual',
            message: 'Interval must be greater than 0',
          })
          return
        }

        actionData.meta = {
          ...actionData.meta,
          intervalId: data.intervalId.trim(),
          intervalMs: data.intervalMs,
          immediate: data.immediate ?? true,
          noOverlap: data.noOverlap ?? true,
        }

        if (data.maxRuns && data.maxRuns > 0) {
          actionData.meta.maxRuns = data.maxRuns
        }
      }

      // Add interval meta for STOP_INTERVAL action
      if (data.actionCode === MOBILE_ACTION_CODE.STOP_INTERVAL) {
        if (!data.intervalId || !data.intervalId.trim()) {
          setError('intervalId' as any, {
            type: 'manual',
            message: 'Interval ID is required for STOP_INTERVAL action',
          })
          return
        }

        actionData.meta = {
          ...actionData.meta,
          intervalId: data.intervalId.trim(),
        }
      }

      // Add meta for SHOW_TOAST action
      if (data.actionCode === MOBILE_ACTION_CODE.SHOW_TOAST) {
        if (!data.toastText1?.trim()) {
          setError('toastText1' as any, {
            type: 'manual',
            message: 'Title (text1) is required for SHOW_TOAST action',
          })
          return
        }
        actionData.meta = {
          ...actionData.meta,
          type: data.toastType || 'error',
          text1: data.toastText1.trim(),
          text2: data.toastText2?.trim() || '',
        }
      }

      // Add snapIndex for OPEN_SHEET action
      if (data.actionCode === MOBILE_ACTION_CODE.OPEN_SHEET) {
        actionData.meta = {
          ...actionData.meta,
          snapIndex: data.snapIndex ?? 1,
        }
      }

      // Add CONDITION action meta
      if (data.actionCode === MOBILE_ACTION_CODE.CONDITION) {
        const ct = data.conditionType || 'js'
        actionData.meta = {
          ...actionData.meta,
          conditionType: ct,
        }
        if (ct === 'js') {
          if (!data.conditionJsCode?.trim()) {
            setError('conditionJsCode' as any, {
              type: 'manual',
              message: 'JavaScript code is required for JS condition',
            })
            return
          }
          actionData.meta.conditionJsCode = data.conditionJsCode.trim()
        } else {
          if (!data.conditionVariableName?.trim()) {
            setError('conditionVariableName' as any, {
              type: 'manual',
              message: 'Variable is required for variable condition',
            })
            return
          }
          actionData.meta.conditionVariableName = data.conditionVariableName.trim()
          if (data.conditionVariableScope && data.conditionVariableScope !== 'local') {
            actionData.meta.conditionVariableScope = data.conditionVariableScope
          }
          if (data.conditionVariableFieldPath?.trim()) {
            actionData.meta.conditionVariableFieldPath = data.conditionVariableFieldPath.trim()
          }
          if (data.conditionVariablePathArray?.length) {
            actionData.meta.conditionVariablePathArray = data.conditionVariablePathArray
          }
        }
        if (data.conditionBranches && data.conditionBranches.length > 0) {
          actionData.meta.conditionBranches = data.conditionBranches.filter(
            b => b.value !== '' && b.conditionBranchId !== ''
          )
        }
        if (data.conditionDefaultBranchId?.trim()) {
          actionData.meta.conditionDefaultBranchId = data.conditionDefaultBranchId.trim()
        }
      }

      // Remove meta if it's empty
      if (Object.keys(actionData.meta || {}).length === 0) {
        delete actionData.meta
      }

      onSave(actionData)
      onClose()
    })(event)
  }

  const handleOpenCommandDialog = () => {
    setShowCommandDialog(true)
  }

  const handleCloseCommandDialog = () => {
    setShowCommandDialog(false)
  }

  const handleChangeCommand = (commands?: GETCommand[] | null) => {
    // Only take the first command (single command selection)
    if (commands && commands.length > 0) {
      setValue('command', commands[0])
    } else {
      setValue('command', undefined)
    }
    setShowCommandDialog(false)
  }

  const handleSaveVariableValue = (code: string) => {
    setValue('variableValue', code)
    setShowVariableValueEditor(false)
  }

  const handleSaveJsCode = (code: string) => {
    setValue('jsCode', code)
    setShowJsCodeEditor(false)
  }

  const handleSaveConditionJsCode = (code: string) => {
    setValue('conditionJsCode', code)
    setShowConditionJsEditor(false)
  }

  const watchedConditionType = watch('conditionType')
  const watchedConditionVariableName = watch('conditionVariableName')
  const watchedConditionBranches = watch('conditionBranches') || []

  const selectedConditionVariable = useMemo(() => {
    if (!watchedConditionVariableName) return null
    return allVariables.find(v => v.internalId === watchedConditionVariableName) || null
  }, [allVariables, watchedConditionVariableName])

  const conditionVariableObjectOption = useMemo(() => {
    const code = (selectedConditionVariable as any)?.objectCode
    return code ? { id: code, label: code } : null
  }, [selectedConditionVariable])

  const conditionVariableDisplayPath = useMemo(() => {
    const pa = watch('conditionVariablePathArray')
    if (!pa || pa.length === 0) return ''
    return pa.map((item: any) => item?.field).filter(Boolean).join('.')
  }, [watch('conditionVariablePathArray')])

  // Build variable types map for type inference
  const buildVariableTypesForInference = (): Record<string, string> => {
    const variableTypesMap: Record<string, string> = {}

    // Add context parameters
    availableContextParams.forEach(param => {
      variableTypesMap[param.name] = param.type
    })

    // Add local variables
    variables.forEach(v => {
      variableTypesMap[`localVariables.${v.internalId}`] = 'any'
    })

    // Add global variables
    globalVariables.forEach(v => {
      variableTypesMap[`globalVariables.${v.internalId}`] = 'any'
    })

    // Add background/sheet variables (SPLIT screen ON_SHEET_OPENED/ON_SHEET_CLOSED)
    backgroundVariables?.forEach(v => {
      variableTypesMap[`backgroundVariables.${v.internalId}`] = 'any'
    })
    sheetFormVariables?.forEach(v => {
      variableTypesMap[`sheetFormVariables.${v.internalId}`] = 'any'
    })

    // Add previousResult type when depends on one action (switch by actionCode)
    if (watchedDependsOn && watchedDependsOn.length === 1) {
      const dependentAction = existingActions.find(a => a.id === watchedDependsOn[0])
      if (dependentAction) {
        switch (dependentAction.actionCode) {
          case MOBILE_ACTION_CODE.EXECUTE_JS: {
            const resultType = dependentAction.meta?.jsResultType || 'any'
            if (resultType !== 'void' && resultType !== 'unknown') {
              variableTypesMap['previousResult'] = resultType
            }
            break
          }
          case MOBILE_ACTION_CODE.EXECUTE_COMMAND: {
            const commandName = (dependentAction.meta?.command as { name?: string } | undefined)?.name
            if (commandName === OBJECT_READ_ALL_COMMAND_NAME) {
              variableTypesMap['previousResult'] = '{ data: any[]; totalPages: number; totalElements: number; currentPage: number; pageSize: number }'
            } else {
              const resultTypeInfo = MOBILE_ACTION_RESULT_TYPES[dependentAction.actionCode]
              if (resultTypeInfo && resultTypeInfo.type !== 'void') {
                if (resultTypeInfo.fields) {
                  const fieldsStr = resultTypeInfo.fields
                    .map(f => `${f.name}: ${f.type}`)
                    .join('; ')
                  variableTypesMap['previousResult'] = `{ ${fieldsStr} }`
                } else {
                  variableTypesMap['previousResult'] = resultTypeInfo.type
                }
              }
            }
            break
          }
          default: {
            const resultTypeInfo = MOBILE_ACTION_RESULT_TYPES[dependentAction.actionCode]
            if (resultTypeInfo && resultTypeInfo.type !== 'void') {
              if (resultTypeInfo.fields) {
                const fieldsStr = resultTypeInfo.fields
                  .map(f => `${f.name}: ${f.type}`)
                  .join('; ')
                variableTypesMap['previousResult'] = `{ ${fieldsStr} }`
              } else {
                variableTypesMap['previousResult'] = resultTypeInfo.type
              }
            }
            break
          }
        }
      }
    }

    return variableTypesMap
  }

  // Component to display inferred type for EXECUTE_JS
  const ExecuteJsTypeDisplay = ({ code, variableTypes }: { code: string; variableTypes: Record<string, string> }) => {
    const { type, fields, isInferring, error } = useInferredType(code, variableTypes, 500)

    if (!code || !code.trim()) {
      return null
    }

    if (isInferring) {
      return (
        <Alert severity="info" sx={{ mt: 1 }}>
          <Typography variant="caption">Analyzing code...</Typography>
        </Alert>
      )
    }

    if (error) {
      return (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <Typography variant="caption">Could not determine type: {error}</Typography>
        </Alert>
      )
    }

    if (type === 'unknown') {
      return null
    }

    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
          Result type: <code>{type}</code>
        </Typography>
      </Alert>
    )
  }

  // Helper to insert parameter into navigationParams field
  const insertParam = (paramName: string) => {
    const currentValue = watch('navigationParams') || ''
    const newValue = currentValue ? `${currentValue}, ${paramName}` : `{ ${paramName} }`
    setValue('navigationParams', newValue)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{action ? 'Edit Action' : 'Add Action'}</DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Controller
            name="id"
            control={control}
            rules={{ required: 'ID is required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Action ID"
                fullWidth
                disabled={!!action}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || 'Unique identifier for this action'}
              />
            )}
          />

          <Controller
            name="actionCode"
            control={control}
            rules={{ required: 'Action code is required' }}
            render={({ field, fieldState }) => (
              <Autocomplete
                value={ACTION_CODE_OPTIONS.find(opt => opt.value === field.value) || null}
                onChange={(_, newValue) => {
                  field.onChange(newValue?.value || '')
                }}
                options={ACTION_CODE_OPTIONS}
                getOptionLabel={option => option.label}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Action Code"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            )}
          />

          <Controller
            name="dependsOn"
            control={control}
            render={({ field, fieldState }) => (
              <Autocomplete
                multiple
                value={field.value}
                onChange={(_, newValue) => {
                  field.onChange(newValue)
                }}
                options={availableActionsForDependency.map(a => a.id)}
                getOptionLabel={option => {
                  const action = availableActionsForDependency.find(a => a.id === option)

                  return action ? `${action.id} (${action.actionCode})` : option
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Depends On"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      'Select actions that must complete before this action runs'
                    }
                  />
                )}
              />
            )}
          />

          {/* Command selector for EXECUTE_COMMAND action */}
          {watchedActionCode === MOBILE_ACTION_CODE.EXECUTE_COMMAND && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Command Configuration
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleOpenCommandDialog}
                sx={{ mb: 1 }}
              >
                {watch('command') ? 'Edit Command' : 'Select Command'}
              </Button>
              {watch('command') && (
                <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" display="block">
                    <strong>Name:</strong> {watch('command')?.name}
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Object:</strong> {watch('command')?.objectCode}
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Method:</strong> {watch('command')?.httpMethod}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Variable selector for CHANGE_VARIABLE_VALUE action */}
          {watchedActionCode === MOBILE_ACTION_CODE.CHANGE_VARIABLE_VALUE && (
            <>
              <Controller
                name="variableName"
                control={control}
                rules={{
                  required: watchedActionCode === MOBILE_ACTION_CODE.CHANGE_VARIABLE_VALUE ? 'Variable is required' : false,
                }}
                render={({ field, fieldState }) => {
                  const watchedScope = watch('variableScope')
                  return (
                  <Autocomplete
                    value={
                      allVariables.find(
                        v =>
                          v.internalId === field.value &&
                          ((v as { variableScope?: string }).variableScope ?? 'local') === watchedScope
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      const scope = ((newValue as { variableScope?: 'local' | 'global' | 'background' | 'sheetForm' })?.variableScope ?? 'local') as 'local' | 'global' | 'background' | 'sheetForm'
                      setValue('variableScope', scope)
                      field.onChange(newValue?.internalId ?? '')
                    }}
                    options={allVariables}
                    groupBy={(option) => {
                      const scope = (option as { variableScope?: string }).variableScope
                      if (scope === 'background') return 'Background Variables'
                      if (scope === 'sheetForm') return 'Sheet Form Variables'
                      return option.isGlobal ? 'Global Variables' : 'Local Variables'
                    }}
                    getOptionLabel={(option) => option.internalId ?? ''}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Variable"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message || 'Select the variable to change (local or global)'}
                        required
                      />
                    )}
                    disabled={allVariables.length === 0}
                  />
                  )
                }}
              />

              {allVariables.length === 0 && (
                <Box sx={{ p: 1.5, bgcolor: 'warning.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="warning.main">
                    No variables available. Create variables in the Variables tab (local) or Mobile Global Variables
                    (global).
                  </Typography>
                </Box>
              )}

              <Box>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setShowVariableValueEditor(true)}
                  startIcon={<CodeIcon />}
                >
                  {watch('variableValue') ? 'Edit Variable Value JavaScript' : 'Add Variable Value JavaScript'}
                </Button>
                {watch('variableValue') && (
                  <Box mt={1} p={1} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                      {watch('variableValue')!.length > 100
                        ? watch('variableValue')!.substring(0, 100) + '...'
                        : watch('variableValue')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* Screen selector for NAVIGATE action */}
          {watchedActionCode === MOBILE_ACTION_CODE.NAVIGATE && (
            <>
              <Controller
                name="screenId"
                control={control}
                rules={{
                  required: watchedActionCode === MOBILE_ACTION_CODE.NAVIGATE ? 'Screen is required for NAVIGATE action' : false,
                }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    value={screens.find((s: MobileScreen) => s.id === field.value) || null}
                    onChange={(_, newValue) => {
                      field.onChange(newValue?.id || '')
                      // Also save screen name for display purposes
                      if (newValue) {
                        setValue('screenName', newValue.name)
                      } else {
                        setValue('screenName', '')
                      }
                    }}
                    options={screens}
                    getOptionLabel={(option: MobileScreen) => `${option.name} (${option.title})`}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Target Screen"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message || 'Select the screen to navigate to'}
                        required
                      />
                    )}
                  />
                )}
              />

              {/* Available context parameters info */}
              {availableContextParams.length > 0 && (
                <Box sx={{
                  p: 1.5,
                  bgcolor: 'info.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'info.main',
                }}>
                  <Typography variant="caption" fontWeight="bold" color="info.main" gutterBottom display="block">
                    💡 Available context parameters:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5, mb: 1 }}>
                    {availableContextParams.map((param) => (
                      <Chip
                        key={param.name}
                        label={`${param.name}: ${param.type}`}
                        size="small"
                        variant="outlined"
                        onClick={() => insertParam(param.name)}
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'info.100' },
                        }}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Click on a parameter to insert it. Example: {`{ item, index }`}
                  </Typography>
                </Box>
              )}

              {/* Navigation params field */}
              <Controller
                name="navigationParams"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Navigation Params (JS)"
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="{ item }"
                    helperText="JavaScript object with parameters to pass to the screen"
                    error={!!fieldState.error}
                  />
                )}
              />

              {/* Modal form options */}
              <Controller
                name="isModal"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value || false}
                        onChange={field.onChange}
                      />
                    }
                    label="Open as modal form"
                  />
                )}
              />

              {watch('isModal') && (
                <>
                  <Controller
                    name="formConfigId"
                    control={control}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        options={forms}
                        getOptionLabel={(option) => option.name}
                        value={forms.find(f => f.id === field.value) || null}
                        onChange={(_, newValue) => {
                          field.onChange(newValue?.id || '')
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Form Configuration"
                            fullWidth
                            required
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || 'Select a form to open in modal'}
                          />
                        )}
                      />
                    )}
                  />

                  <Controller
                    name="modalTitle"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Modal Title (optional)"
                        fullWidth
                        helperText="Custom title for the modal. If empty, form name will be used."
                      />
                    )}
                  />

                  <Controller
                    name="onSubmitVariable"
                    control={control}
                    render={({ field }) => {
                      const allVars = [...(variables || []), ...(globalVariables || [])]
                      return (
                        <Autocomplete
                          options={allVars}
                          getOptionLabel={(option) => option.internalId ?? ''}
                          value={allVars.find(v => v.internalId === field.value) || null}
                          onChange={(_, newValue) => {
                            field.onChange(newValue?.internalId ?? '')
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Result Variable (optional)"
                              fullWidth
                              helperText="Variable to store the form submission result"
                            />
                          )}
                        />
                      )
                    }}
                  />
                </>
              )}
            </>
          )}

          {watchedActionCode === MOBILE_ACTION_CODE.EXECUTE_JS && (
            <>
              <Box>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setShowJsCodeEditor(true)}
                  startIcon={<CodeIcon />}
                >
                  {watch('jsCode') ? 'Edit JavaScript Expression' : 'Add JavaScript Expression'}
                </Button>
                {watch('jsCode') && (
                  <Box mt={1}>
                    <Box p={1} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1, mb: 1 }}>
                      <Typography
                        variant="caption"
                        component="pre"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 11,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          margin: 0,
                        }}
                      >
                        {watch('jsCode')!.length > 100
                          ? watch('jsCode')!.substring(0, 100) + '...'
                          : watch('jsCode')}
                      </Typography>
                    </Box>
                    <ExecuteJsTypeDisplay
                      code={watch('jsCode') || ''}
                      variableTypes={buildVariableTypesForInference()}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* START_INTERVAL action fields */}
          {watchedActionCode === MOBILE_ACTION_CODE.START_INTERVAL && (
            <>
              <Controller
                name="intervalId"
                control={control}
                rules={{
                  required: 'Interval ID is required for START_INTERVAL',
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Interval ID"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || 'Unique identifier for this timer'}
                    required
                    placeholder="e.g., gps_timer, sync_timer"
                  />
                )}
              />

              <Controller
                name="intervalMs"
                control={control}
                rules={{
                  required: 'Interval is required for START_INTERVAL',
                  min: { value: 100, message: 'Interval must be at least 100ms' },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Interval (ms)"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || 'Interval in milliseconds (e.g., 5000 = 5 seconds)'}
                    required
                    onChange={(e) => field.onChange(parseInt(e.target.value) || undefined)}
                  />
                )}
              />

              <Controller
                name="immediate"
                control={control}
                render={({ field }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="checkbox"
                      checked={field.value ?? true}
                      onChange={(e) => field.onChange(e.target.checked)}
                      id="immediate-checkbox"
                    />
                    <label htmlFor="immediate-checkbox">
                      <Typography variant="body2">
                        Execute immediately (trigger ON_INTERVAL event right away, then repeat on interval)
                      </Typography>
                    </label>
                  </Box>
                )}
              />

              <Controller
                name="noOverlap"
                control={control}
                render={({ field }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="checkbox"
                      checked={field.value ?? true}
                      onChange={(e) => field.onChange(e.target.checked)}
                      id="noOverlap-checkbox"
                    />
                    <label htmlFor="noOverlap-checkbox">
                      <Typography variant="body2">
                        No overlap (skip tick if previous ON_INTERVAL flow is still running)
                      </Typography>
                    </label>
                  </Box>
                )}
              />

              <Controller
                name="maxRuns"
                control={control}
                rules={{
                  min: { value: 1, message: 'Max runs must be at least 1' },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Max Runs (optional)"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || 'Maximum number of ticks (leave empty for unlimited)'}
                    onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                )}
              />

              <Box sx={{
                p: 1.5,
                bgcolor: 'info.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'info.main',
              }}>
                <Typography variant="caption" fontWeight="bold" color="info.main" gutterBottom display="block">
                  💡 How it works:
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  START_INTERVAL creates a timer that triggers <strong>ON_INTERVAL</strong> events periodically.
                  <br />
                  Create a separate EventFlow with eventCode=ON_INTERVAL and intervalId="{watch('intervalId') || 'your_timer_id'}" to handle each tick.
                  <br />
                  Example: START_INTERVAL (on_init) → triggers → ON_INTERVAL flow (get_position → send_data) every {watch('intervalMs') || 'X'}ms.
                </Typography>
              </Box>
            </>
          )}

          {/* STOP_INTERVAL action fields */}
          {watchedActionCode === MOBILE_ACTION_CODE.STOP_INTERVAL && (
            <>
              <Controller
                name="intervalId"
                control={control}
                rules={{
                  required: 'Interval ID is required for STOP_INTERVAL',
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Interval ID"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || 'ID of the timer to stop (must match START_INTERVAL)'}
                    required
                    placeholder="e.g., gps_timer, sync_timer"
                  />
                )}
              />
            </>
          )}

          {/* CONDITION action fields */}
          {watchedActionCode === MOBILE_ACTION_CODE.CONDITION && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Condition Configuration
              </Typography>

              {/* Condition Type toggle */}
              <Controller
                name="conditionType"
                control={control}
                render={({ field }) => (
                  <ToggleButtonGroup
                    value={field.value || 'js'}
                    exclusive
                    onChange={(_, val) => { if (val) field.onChange(val) }}
                    size="small"
                    fullWidth
                  >
                    <ToggleButton value="js">JavaScript</ToggleButton>
                    <ToggleButton value="variable">Variable</ToggleButton>
                  </ToggleButtonGroup>
                )}
              />

              {/* JS condition */}
              {watchedConditionType === 'js' && (
                <Box>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setShowConditionJsEditor(true)}
                    startIcon={<CodeIcon />}
                  >
                    {watch('conditionJsCode') ? 'Edit Condition JS' : 'Add Condition JS'}
                  </Button>
                  {watch('conditionJsCode') && (
                    <Box mt={1} p={1} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                        {watch('conditionJsCode')!.length > 120
                          ? watch('conditionJsCode')!.substring(0, 120) + '...'
                          : watch('conditionJsCode')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Variable condition */}
              {watchedConditionType === 'variable' && (
                <>
                  <Controller
                    name="conditionVariableName"
                    control={control}
                    render={({ field, fieldState }) => {
                      const watchedScope = watch('conditionVariableScope')
                      return (
                        <Autocomplete
                          value={
                            allVariables.find(
                              v =>
                                v.internalId === field.value &&
                                ((v as { variableScope?: string }).variableScope ?? 'local') === watchedScope
                            ) || null
                          }
                          onChange={(_, newValue) => {
                            const scope = ((newValue as any)?.variableScope ?? 'local') as 'local' | 'global' | 'background' | 'sheetForm'
                            setValue('conditionVariableScope', scope)
                            field.onChange(newValue?.internalId ?? '')
                            setValue('conditionVariableFieldPath', '')
                            setValue('conditionVariablePathArray', undefined)
                          }}
                          options={allVariables}
                          groupBy={(option) => {
                            const scope = (option as { variableScope?: string }).variableScope
                            if (scope === 'background') return 'Background Variables'
                            if (scope === 'sheetForm') return 'Sheet Form Variables'
                            return option.isGlobal ? 'Global Variables' : 'Local Variables'
                          }}
                          getOptionLabel={(option) => option.internalId ?? ''}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label="Condition Variable"
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message || 'Variable whose value determines the branch'}
                            />
                          )}
                        />
                      )
                    }}
                  />

                  {/* Field path picker for object variables */}
                  {conditionVariableObjectOption && (
                    <ModalRefPathPicker
                      label="Field Path (drill into object)"
                      value={
                        watch('conditionVariablePathArray')?.length
                          ? {
                            path: conditionVariableDisplayPath,
                            pathArray: watch('conditionVariablePathArray') || [],
                          }
                          : undefined
                      }
                      onChange={(next: RefPathValue | undefined) => {
                        const pa = next?.pathArray || null
                        setValue('conditionVariablePathArray', pa || undefined)
                        const fp = pa ? pa.map((item: any) => item?.field).filter(Boolean).join('.') : ''
                        setValue('conditionVariableFieldPath', fp)
                      }}
                      hasClearInInput
                      isEdit={!!watch('conditionVariablePathArray')?.length}
                      refPickAllowed
                      object={conditionVariableObjectOption}
                      objectOptions={[conditionVariableObjectOption]}
                    />
                  )}
                </>
              )}

              {/* Branches table */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Branches</Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const current = watch('conditionBranches') || []
                      setValue('conditionBranches', [...current, { value: '', conditionBranchId: '' }])
                    }}
                  >
                    Add Branch
                  </Button>
                </Box>
                {watchedConditionBranches.map((branch, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      label="Value"
                      value={branch.value}
                      onChange={e => {
                        const updated = [...watchedConditionBranches]
                        updated[idx] = { ...updated[idx], value: e.target.value }
                        setValue('conditionBranches', updated)
                      }}
                      sx={{ flex: 1 }}
                      placeholder='e.g., true, false, admin'
                    />
                    <Autocomplete
                      size="small"
                      sx={{ flex: 2 }}
                      options={onConditionFlows}
                      value={onConditionFlows.find(f => f.conditionBranchId === branch.conditionBranchId) || null}
                      onChange={(_, newValue) => {
                        const updated = [...watchedConditionBranches]
                        updated[idx] = { ...updated[idx], conditionBranchId: newValue?.conditionBranchId || '' }
                        setValue('conditionBranches', updated)
                      }}
                      getOptionLabel={f => `${f.conditionBranchId} (${f.id})`}
                      renderInput={params => <TextField {...params} label="Branch Flow" placeholder="Select on_condition flow" />}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        const updated = watchedConditionBranches.filter((_, i) => i !== idx)
                        setValue('conditionBranches', updated)
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {watchedConditionBranches.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No branches. Add on_condition flows first, then map values to branches here.
                  </Typography>
                )}
              </Box>

              {/* Default branch */}
              <Autocomplete
                size="small"
                options={onConditionFlows}
                value={onConditionFlows.find(f => f.conditionBranchId === watch('conditionDefaultBranchId')) || null}
                onChange={(_, newValue) => {
                  setValue('conditionDefaultBranchId', newValue?.conditionBranchId || '')
                }}
                getOptionLabel={f => `${f.conditionBranchId} (${f.id})`}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Default Branch (optional)"
                    helperText="Fallback branch when no value matches"
                  />
                )}
              />

              <Box sx={{ p: 1.5, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
                <Typography variant="caption" fontWeight="bold" color="info.main" gutterBottom display="block">
                  How it works:
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  1. Create on_condition event flows with unique Branch IDs (e.g. branch_true, branch_false).
                  <br />
                  2. Configure the condition (JS or variable) that returns a value.
                  <br />
                  3. Map each possible value to a branch flow in the table above.
                  <br />
                  4. At runtime, the matching branch flow's actions will execute.
                </Typography>
              </Box>
            </>
          )}

          {/* SHOW_TOAST action fields */}
          {watchedActionCode === MOBILE_ACTION_CODE.SHOW_TOAST && (
            <>
              <Controller
                name="toastType"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    value={{ value: field.value || 'error', label: (field.value || 'error').charAt(0).toUpperCase() + (field.value || 'error').slice(1) }}
                    onChange={(_, newValue) => field.onChange(newValue?.value || 'error')}
                    options={[
                      { value: 'success', label: 'Success' },
                      { value: 'error', label: 'Error' },
                      { value: 'info', label: 'Info' },
                    ]}
                    getOptionLabel={option => option.label}
                    renderInput={params => (
                      <TextField {...params} label="Toast Type" />
                    )}
                  />
                )}
              />
              <Controller
                name="toastText1"
                control={control}
                rules={{ required: 'Title is required for SHOW_TOAST' }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Title (text1)"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || 'Main toast message'}
                    required
                  />
                )}
              />
              <Controller
                name="toastText2"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Subtitle (text2)"
                    helperText="Optional secondary message"
                  />
                )}
              />
            </>
          )}

          {/* OPEN_SHEET action fields */}
          {watchedActionCode === MOBILE_ACTION_CODE.OPEN_SHEET && (
            <Controller
              name="snapIndex"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="number"
                  label="Snap index"
                  inputProps={{ min: 0 }}
                  value={field.value ?? 1}
                  onChange={e => field.onChange(parseInt(e.target.value, 10) || 0)}
                  helperText="0-based index of snap point to open (e.g. 0=first, 1=second)"
                />
              )}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            Save
          </Button>
        </DialogActions>
      </form>

      {/* AddCommandsDialog for EXECUTE_COMMAND action - limited to single command */}
      {showCommandDialog && (
        <AddCommandsDialog
          objectCode="mobile_action"
          commands={watch('command') ? [watch('command')!] : []}
          onCloseDialog={handleCloseCommandDialog}
          onChangeCommands={handleChangeCommand}
          scriptVariables={scriptVariablesForCommands}
          scriptFieldsInfo={scriptFieldsInfoForCommands}
          commandPickerControllerProps={{
            isSingle: true, // Limit to single command selection
            isTypeHidden: true, // Hide type field
            canChangeRefObject: true, // Allow changing object
          }}
        />
      )}

      {/* ScriptValueDialog for Variable Value */}
      {showVariableValueEditor && (
        <ScriptValueDialog
          isShow
          value={watch('variableValue') || ''}
          title="Edit Variable Value JavaScript"
          onClose={() => setShowVariableValueEditor(false)}
          onSave={handleSaveVariableValue}
          language="js"
          width={800}
          variables={scriptVariablesForCommands}
          fieldsInfo={scriptFieldsInfoForCommands}
          examples={[
            {
              title: 'Simple value',
              code: 'return eventData.formData?.userId || 0',
            },
            {
              title: 'From item',
              code: 'return eventData.item?.id || null',
            },
            {
              title: 'Conditional value',
              code: 'return eventData.formData?.status === "active" ? 1 : 0',
            },
            {
              title: 'With index',
              code: 'return index + 1',
            },
            {
              title: 'Using previous result',
              code: 'return eventData.previousResult?.latitude + ", " + eventData.previousResult?.longitude',
            },
          ]}
        />
      )}

      {/* ScriptValueDialog for EXECUTE_JS code */}
      {showJsCodeEditor && (
        <ScriptValueDialog
          isShow
          value={watch('jsCode') || ''}
          title="Edit JavaScript Expression"
          onClose={() => setShowJsCodeEditor(false)}
          onSave={handleSaveJsCode}
          language="js"
          width={800}
          variables={scriptVariablesForCommands}
          fieldsInfo={scriptFieldsInfoForCommands}
          examples={[
            {
              title: 'Simple return',
              code: 'return 42',
            },
            {
              title: 'Return object',
              code: 'return { x: 1, y: 2 }',
            },
            {
              title: 'Using previous result',
              code: 'return eventData.previousResult?.latitude + ", " + eventData.previousResult?.longitude',
            },
            {
              title: 'Using context',
              code: 'return eventData.item?.id || null',
            },
          ]}
        />
      )}

      {/* ScriptValueDialog for CONDITION JS code */}
      {showConditionJsEditor && (
        <ScriptValueDialog
          isShow
          value={watch('conditionJsCode') || ''}
          title="Edit Condition JavaScript"
          onClose={() => setShowConditionJsEditor(false)}
          onSave={handleSaveConditionJsCode}
          language="js"
          width={800}
          variables={scriptVariablesForCommands}
          fieldsInfo={scriptFieldsInfoForCommands}
          examples={[
            {
              title: 'Boolean check',
              code: 'return eventData.formData?.status === "active"',
            },
            {
              title: 'Return string value',
              code: 'return eventData.item?.role || "guest"',
            },
            {
              title: 'Based on previous result',
              code: 'return eventData.previousResult?.data?.length > 0 ? "has_data" : "empty"',
            },
          ]}
        />
      )}

    </Dialog>
  )
}

// Helper function to check circular dependencies
function checkCircularDependency(
  actionId: string,
  dependsOn: string[],
  allActions: MobileAction[],
): boolean {
  const visited = new Set<string>()
  const recursionStack = new Set<string>()

  function hasCycle(currentId: string): boolean {
    if (recursionStack.has(currentId)) {
      return true
    }

    if (visited.has(currentId)) {
      return false
    }

    visited.add(currentId)
    recursionStack.add(currentId)

    const action = allActions.find(a => a.id === currentId)
    if (action?.dependsOn) {
      for (const depId of action.dependsOn) {
        if (hasCycle(depId)) {
          return true
        }
      }
    }

    recursionStack.delete(currentId)

    return false
  }

  // Check if any of the dependencies would create a cycle
  for (const depId of dependsOn) {
    const action = allActions.find(a => a.id === depId)
    if (action?.dependsOn?.includes(actionId)) {
      return true
    }

    if (hasCycle(depId)) {
      return true
    }
  }

  return false
}

